<template>
  <div>
    <div
      v-if="failed"
      class="uk-alert uk-alert-warning"
      v-html="failedMessage"
    />
    <div v-show="showUploader && videoIsUpload">
      <span
        :id="'uploader-' + video.data.id"
        class="uk-button uk-button-primary"
      ><i class="uk-icon-cloud-upload" /> {{ $t('upload_new_source') }}</span>
      <span
        v-if="canRetry"
        class="uk-button uk-button-primary"
        @click="retryUpload"
      ><i class="uk-icon-refresh" /> {{ $t('retry_upload') }}</span>
    </div>
    <div v-show="paused">
      <p class="uk-alert uk-alert-warning">
        {{ $t('video_upload_paused') }} <span
          class="uk-button"
          @click="retryUpload"
        >{{ $t('video_upload_paused_resume') }}</span>
      </p>
    </div>
    <div v-if="videoIsEmbed">
      <div
        v-if="!videoIsEmbedding"
        class="uk-form uk-form-stacked"
      >
        <div class="uk-form-row">
          <label
            for=""
            class="uk-form-label"
          >{{ $t('embed_label') }}</label>
          <div class="uk-form-controls">
            <input
              ref="embed"
              type="text"
              name="embed"
              :placeholder="$t('embed_placeholder')"
              class="uk-width-8-10"
            >
            <button
              class="uk-button uk-button-primary"
              @click="submitEmbed"
            >
              {{ $t('embed_button') }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="videoIsEmbedding">
        <p><span class="uk-icon-spinner uk-icon-spin" /> {{ $t('embedding_video') }}</p>
      </div>
    </div>

    <button
      id="uploader_browse_files_video-replacer"
      style="display: none;"
    />
  </div>
</template>

<script>
    import VideoModel from '../../models/VideoModel';
    import { queueFile, getUploader, createUploader } from '../../lib/upload';
    import spr from 'sprintf-js';
    import VideoFileHandler from '../../lib/upload/VideoFileHandler';
  export default {
    name: 'Upload',
    props: {
      video: VideoModel
    },
    data() {
      return {
        forceShow: false,
        retryMaxAttempts: 2,
        retryAttempts: 0,
        fileSelector: null,
        uploaderName: this.uploaderName,
      };
    },
    computed: {
      videoIsEmbed() {
        return this.video.data.type === 'embed';
      },
      videoIsUpload() {
        return ['upload', 'entry'].indexOf(this.video.data.type) > -1 || this.video.data.source_file_status === 'not_set';
      },
      userContext() {
        return this.$store.getters['context/user'];
      },
      showUploader() {
        return ((this.userContext.canOverwriteVideo || this.video.data.type === 'entry') && this.video.sourceCanBeReplaced()) || !this.video.isComplete();
      },
      videoIsEmbedding() {
        return this.videoIsEmbed && this.video.isUploading();
      },
      canRetry() {
        return this.retryAttempts <= this.retryMaxAttempts && this.video.isInvalid();
      },
      paused() {
        return this.video.data.source_file_status === 'upload_paused';
      },
      failed() {
        return this.video.data.source_file_status === 'failed';
      },
      failedMessage() {
        return '<span class="uk-icon-exclamation-triangle"></span> ' + spr.sprintf(this.$t('server_upload_failure'), this.video.data.video_id);
      }
    },
    watch: {
      'video.isDeleted': {
        immediate: true,
        deep: true,
        handler(newVal, oldVal) {
          if (newVal === true) {
            this.destroy();
          }
        }
      }
    },
    created() {
      if (!this.$store.getters['modules/getModuleContext']('uploader')) {
        this.$store.dispatch('modules/loadModule', 'uploader').then(resp => {
          this.$i18n.add(resp.i18n.locale, resp.i18n.strings);
        });
      }

      if (!this.$store.getters['videos/byId'](this.video.data.id)) {
        this.$store.dispatch('videos/addInstance', this.video);
      }
    },
    beforeDestroy() {
      this.destroy();
    },
    mounted() {
      // If the default uploader isn't available, create a local one.
      if (!getUploader(this.uploaderName)) {
        this.uploaderName = 'video-replacer';
        createUploader(this.uploaderName, this.$store, [new VideoFileHandler()], {
          headers: {'Authorization': 'Bearer ' + this.$store.getters['context/apiToken']}
        });
      }

      this.fileSelector = new moxie.file.FileInput({
        browse_button: 'uploader-' + this.video.data.id,
        multiple: false
      });

      this.fileSelector.onchange = (e) => {
        queueFile(this.uploaderName, this.fileSelector.files[0], {
          upload_type: 'video',
          video_id: this.video.data.id,
          upload_url: BASE_URI + '/api/videos/' + this.video.data.id + '/source',
        });
      };

      this.fileSelector.init();
    },
    methods: {
      retryUpload() {
        this.retryAttempts++;

        getUploader(this.uploaderName).resume();
      },
      pauseUpload() {
        getUploader(this.uploaderName).pause();
      },
      destroy() {
        if (getUploader(this.uploaderName) && this.video.isUploading()) {
          this.$store.dispatch('videos/update', {
            local: true,
            key: 'source_file_status',
            value: 'ok',
            id: this.video.data.id,
          });
          this.$store.dispatch('videos/setUploadProgress', {
            id: this.video.data.id,
            progress: 0,
          });
          getUploader(this.uploaderName).cancelCurrentFile();
        }
      },
      processEmbed(embed, overwrite) {
        const id = this.video.data.id;

        this.$store.dispatch('videos/setEmbedSource', {
          id: this.video.data.id,
          embed: embed,
          overwrite_data: overwrite || false
        }).then(() => {
          this.$store.dispatch('videos/syncSourceStatusUntilComplete', this.video.data.id);
        });
      },
      submitEmbed(e) {
        const input = this.$refs['embed'];
        if (input.value) {
          this.processEmbed(input.value);
          input.value = '';
        }
      }
    }
  };
</script>

<style scoped>

</style>
